import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  subscribeToProjects,
  subscribeToActiveClockIn,
  subscribeToRecentTimeRecords,
  clockIn as performClockIn,
  clockInWithoutProject as performClockInWithoutProject,
  clockOut as performClockOut,
  assignProjectToRecord,
  getTodayWorkingMinutes,
  getThisWeekWorkingMinutes,
  getThisMonthWorkingMinutes,
} from '@/lib/timeTracking';
import type { Project, TimeRecord } from '@/types';

export const useTimeTracker = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeRecord, setActiveRecord] = useState<TimeRecord | null>(null);
  const [recentRecords, setRecentRecords] = useState<TimeRecord[]>([]);
  const [todayMinutes, setTodayMinutes] = useState(0);
  const [thisWeekMinutes, setThisWeekMinutes] = useState(0);
  const [thisMonthMinutes, setThisMonthMinutes] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // プロジェクト一覧の購読
    const unsubProjects = subscribeToProjects(user.uid, 'active', (data) => {
      setProjects(data);
    });

    // アクティブな出勤記録の購読
    const unsubActive = subscribeToActiveClockIn(user.uid, (data) => {
      setActiveRecord(data);
    });

    // 最近のレコードの購読
    const unsubRecent = subscribeToRecentTimeRecords(user.uid, 10, (data) => {
      setRecentRecords(data);
    });

    // 今日の稼働時間の購読
    const unsubToday = getTodayWorkingMinutes(user.uid, (minutes) => {
      setTodayMinutes(minutes);
    });

    // 今週の稼働時間の購読
    const unsubWeek = getThisWeekWorkingMinutes(user.uid, (minutes) => {
      setThisWeekMinutes(minutes);
    });

    // 今月の稼働時間の購読
    const unsubMonth = getThisMonthWorkingMinutes(user.uid, (minutes) => {
      setThisMonthMinutes(minutes);
    });

    setIsLoading(false);

    return () => {
      unsubProjects();
      unsubActive();
      unsubRecent();
      unsubToday();
      unsubWeek();
      unsubMonth();
    };
  }, [user]);

  const clockIn = async (projectId: string) => {
    if (!user || isProcessing) return;

    const project = projects.find((p) => p.id === projectId);
    if (!project) {
      throw new Error('プロジェクトが見つかりません');
    }

    setIsProcessing(true);
    try {
      await performClockIn(user.uid, {
        id: project.id,
        name: project.name,
        code: project.code,
        client: project.clientId,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 🆕 プロジェクト未選択で出勤
   */
  const clockInWithoutProject = async () => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await performClockInWithoutProject(user.uid);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 🆕 出勤中のレコードにプロジェクトを紐付け
   */
  const assignProject = async (recordId: string, projectId: string) => {
    if (!user || isProcessing) return;

    const project = projects.find((p) => p.id === projectId);
    if (!project) {
      throw new Error('プロジェクトが見つかりません');
    }

    setIsProcessing(true);
    try {
      await assignProjectToRecord(user.uid, recordId, {
        id: project.id,
        name: project.name,
        code: project.code,
        client: project.clientId,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const clockOut = async () => {
    if (!user || !activeRecord || isProcessing) return;

    setIsProcessing(true);
    try {
      await performClockOut(user.uid, activeRecord.id);
    } finally {
      setIsProcessing(false);
    }
  };

  const getCurrentWorkingMinutes = (): number => {
    if (!activeRecord?.clockInTime) return 0;
    const now = Date.now();
    const start = activeRecord.clockInTime.toMillis();
    return Math.floor((now - start) / 60000);
  };

  return {
    projects,
    activeRecord,
    recentRecords,
    todayMinutes,
    thisWeekMinutes,
    thisMonthMinutes,
    isLoading,
    isProcessing,
    clockIn,
    clockInWithoutProject,
    assignProject,
    clockOut,
    getCurrentWorkingMinutes,
  };
};
